import Vue from "vue";

// modules
import swal from "sweetalert2";
import toastr from "toastr";

// plugins
import VueRouter from "vue-router";
import VueQuillEditor from "vue-quill-editor";
import VueMoment from "vue-moment";
import momentTimezone from "moment-timezone";
import VueResource from "vue-resource";
import Vue2Filters from "vue2-filters";
import VeeValidate from "vee-validate";
import BootstrapVue from "bootstrap-vue";
import VueProgressBar from "vue-progressbar";
import VueInstant from "vue-instant/dist/vue-instant.common";
import VueTelInput from "vue-tel-input";
import Switches from "vue-switches";
import { Drag, Drop } from "vue-drag-drop";
import vuePerfectScrollbar from "vue-perfect-scrollbar";
import VueApexCharts from "vue-apexcharts";
import VueFusionCharts from "vue-fusioncharts";
import FusionCharts from "fusioncharts";
import Charts from "fusioncharts/fusioncharts.charts";
import ZoomLine from "fusioncharts/fusioncharts.zoomline";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";
import VuePowerCharts from "fusioncharts/fusioncharts.powercharts";
import VueWordCloud from "vuewordcloud";
import infiniteScroll from "vue-infinite-scroll";
import VueSignaturePad from "vue-signature-pad";
import VueCryptojs from "vue-cryptojs";
import { Quill } from "vue-quill-editor";
// app
import store from "@/vuex/store";
import routes from "@/routes";
import { beforeEach } from "@/routes/hooks";
import idleLogout from "@/utils/idle";
import api from "@/api";

// recursive components
import JsonTree from "@/components/common/json-tree";
import "@/constants/filter";
import "@/constants/directive";
import "vue2-timepicker/dist/VueTimepicker.css";
import colxx from "@/components/common/colxx";
import { Widget } from "@/components/services/participant-service";

//locale
import en from "./locales/en.json";
import es from "./locales/es.json";
import { defaultLocale } from "./constants/config";
import VueI18n from "vue-i18n";
import { datadogRum } from "@datadog/browser-rum";
import { sanitizeInput } from "./utils/sanitizeInput";

Vue.component("JsonTree", JsonTree);
Vue.component("Switches", Switches);
Vue.component("drag", Drag);
Vue.component("drop", Drop);
Vue.component("b-colxx", colxx);
Vue.component("vue-perfect-scrollbar", vuePerfectScrollbar);

// CSS
require("quill/dist/quill.core.css");
require("quill/dist/quill.snow.css");
require("quill/dist/quill.bubble.css");

require("toastr");
require("toastr/build/toastr.min.css");

require("sweetalert2/dist/sweetalert2.min.css");

require("bootstrap");
require("bootstrap/dist/css/bootstrap.css");
require("bootstrap-vue/dist/bootstrap-vue.css");
require("@/assets/fontawesome.css");

// Options
toastr.options = {
  closeButton: true,
  newestOnTop: false,
  progressBar: false,
  positionClass: "toast-bottom-center",
  preventDuplicates: true,
  showDuration: 500,
  hideDuration: 500,
  timeOut: 2000,
  extendedTimeOut: 2000
};

const VueProgressBarOptions = {
  color: "#2ecc71",
  failedColor: "#f16559",
  thickness: "2px",
  transition: {
    speed: "0.2s",
    opacity: "0.5s",
    termination: 300
  },
  autoRevert: true,
  location: "top",
  inverse: false
};

Vue.use(VueCryptojs);
Vue.use(VueSignaturePad);
Vue.use(VueProgressBar, VueProgressBarOptions);
Vue.use(VueRouter);
Vue.use(VueQuillEditor);
Vue.use(VueMoment, {
  momentTimezone
});
Vue.use(VueResource);
Vue.use(Vue2Filters);
Vue.use(VeeValidate, {
  fieldsBagName: "veeFields"
});
Vue.use(VueInstant);
Vue.use(BootstrapVue);
Vue.use(VueI18n);

// apex chart
Vue.use(VueApexCharts);
Vue.component("apexchart", VueApexCharts);

// wordcloud
Vue.use(VueWordCloud);
Vue.component(VueWordCloud.name, VueWordCloud);

Vue.use(VueFusionCharts, FusionCharts, VuePowerCharts, Charts, ZoomLine, FusionTheme);
const fusionKey = `${JSON.stringify(process.env.VUE_FUSION_LICENSE)}`;
FusionCharts.options.license({
  key: fusionKey,
  creditLabel: false
});

const messages = { en: en, es: es };
const locale = defaultLocale;
// const locale = (localStorage.getItem('currentLanguage') && localeOptions.filter(x => x.id === localStorage.getItem('currentLanguage')).length > 0) ? localStorage.getItem('currentLanguage') : defaultLocale;
const i18n = new VueI18n({
  locale: locale,
  fallbackLocale: "en",
  messages
});

Vue.use(VueTelInput);

Vue.use(infiniteScroll);

const router = new VueRouter({
  routes
});
router.beforeEach(beforeEach({ store, api, toastr }));

//Initialize only when the application id is setup this way it will not run on local and the envs where the id is not setup
if (process.env.VUE_APP_DD_WEB_PORTAL_APP_ID && process.env.VUE_APP_DD_WEB_PORTAL_APP_ID != "") {
  datadogRum.init({
    applicationId: window.location.href.includes("/participants/web/") ? process.env.VUE_APP_DD_PWEB_APP_ID : process.env.VUE_APP_DD_WEB_PORTAL_APP_ID,
    clientToken: window.location.href.includes("/participants/web/") ? process.env.VUE_APP_DD_PWEB_CLIENT_TOKEN : process.env.VUE_APP_DD_WP_CLIENT_TOKEN,
    site: "datadoghq.com",
    service: window.location.href.includes("/participants/web/") ? "thread-web" : "thread-web-portal",
    env: process.env.VUE_APP_ENV,
    sampleRate: 100,
    trackInteractions: true,
    replaySampleRate: 0,
    allowedTracingOrigins: [process.env.VUE_APP_API_BASE_URL, process.env.VUE_APP_API_STUDY_RESPONSE_BASE_URL, process.env.VUE_APP_API_ACTIVITY_BASE_URL, process.env.VUE_APP_API_ANALYTICS_BASE_URL, process.env.VUE_APP_API_PARTICIPANT_WEB_BASE_URL, process.env.VUE_APP_SERVERLESS_API_BASE_URL, process.env.VUE_APP_SERVERLESS_API_US_BASE_URL, process.env.VUE_APP_API_INTEGRATIONS_BASE_URL]
  });
}

Vue.use({
  install(VueInstance) {
    api.setupInterceptors({
      http: VueInstance.http,
      toastr,
      store,
      VueInstance
    });
    VueInstance.prototype.$swal = swal;
    VueInstance.prototype.$toastr = toastr;
    VueInstance.prototype.$api = api;
    VueInstance.prototype.$sanitize = sanitizeInput;
  }
});

// setup

export default () => {
  const widget = new Widget();
  idleLogout({
    swal,
    store
  });
  store.dispatch("setupLocalStorageUserData");
  store.dispatch("setupLocalStorageEDCData");

  // offline events

  if (!window.navigator.onLine) {
    const message = widget.translate("pweb_no_internet_connection", "Sorry! No internet connection.");
    toastr.warning(message);
  }
  window.addEventListener("online", () => {
    let message = null;
    if (window.location.href.includes("/participants/web/")) {
      message = widget.translate("pweb_internet_active", "Glad to see you back. Please continue with your activities.");
    } else {
      message = "Welcome back, unfortunately we lost you, Continue with your Research!!";
    }
    toastr.success(message);
  });
  window.addEventListener("offline", () => {
    let message = null;
    if (window.location.href.includes("/participants/web/")) {
      message = widget.translate("pweb_no_internet_connection", "No internet connection detected. Please try again after a connection is established.");
    } else {
      message = "Sorry! No internet connection.";
    }
    toastr.warning(message);
  });

  return {
    i18n,
    router,
    store
  };
};

export { router, toastr, swal, store, i18n };

//To use inline styles rather than quill classes for align
const AlignStyle = Quill.import("attributors/style/align");
Quill.register(AlignStyle, true);
